'use client'
import cn from 'classnames'
import { motion } from 'motion/react'
import { type FC, useState } from 'react'
import { IconComponent } from '@/components/Icon/Icon'
import type { Content } from '@/content-types'
import { asText } from '@/lib/content-fields'
import { RichText, type SliceComponentProps } from '@/lib/rich-text'
import { getBackgroundColor } from '@/utils/helpers'

/**
 * Props for `FaQ`.
 */
export type FaQProps = SliceComponentProps<Content.FaQSlice>

/**
 * Component for "FaQ" Slices.
 */
const FaQ: FC<FaQProps> = ({ slice }) => {
  // State to track which FAQ items are open
  const [openItems, setOpenItems] = useState<number[]>([])

  // Toggle function to open/close FAQ items
  const toggleItem = (index: number) => {
    setOpenItems((prevOpenItems) => {
      if (prevOpenItems.includes(index)) {
        // Remove item if already open
        return prevOpenItems.filter((item) => item !== index)
      } else {
        // Add item if closed
        return [...prevOpenItems, index]
      }
    })
  }

  // Check if an item is open
  const isItemOpen = (index: number) => openItems.includes(index)

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={getBackgroundColor(slice.primary.background)}
    >
      <div className="container flex flex-col gap-8 md:gap-2.5 md:space-y-0">
        <RichText
          field={slice.primary.title}
          components={{
            heading2: ({ children }) => <h2 className="section-title">{children}</h2>,
          }}
        />
        <RichText
          field={slice.primary.description}
          components={{
            paragraph: ({ children }) => <p className="mt-4 text-lg">{children}</p>,
          }}
        />
        <div className="flex flex-col pt-10">
          {slice.primary.faq.map((item, index) => (
            <div key={asText(item.question)} className="border-b border-[#dedede] last:border-b-0">
              <button
                type="button"
                onClick={() => toggleItem(index)}
                aria-expanded={isItemOpen(index)}
                className={cn(
                  'flex w-full cursor-pointer items-center justify-between text-left',
                  isItemOpen(index) ? 'pt-6 pb-2' : 'py-6'
                )}
              >
                <div className="flex w-full items-center gap-x-5">
                  {isItemOpen(index) ? (
                    <IconComponent
                      icon="Minus"
                      size="base"
                      className="text-primary shrink-0 transition-transform duration-300"
                    />
                  ) : (
                    <IconComponent
                      icon="Plus"
                      size="base"
                      className="text-primary shrink-0 transition-transform duration-300"
                    />
                  )}

                  <RichText
                    field={item.question}
                    components={{
                      paragraph: ({ children }) => <p className="text-xl font-semibold">{children}</p>,
                    }}
                  />
                </div>
              </button>
              {/* Always rendered so the answers are in the HTML for search engines; only the reveal is animated. */}
              <motion.div
                initial={false}
                animate={
                  isItemOpen(index)
                    ? { height: 'auto', marginBottom: '20px', opacity: 1 }
                    : { height: 0, marginBottom: 0, opacity: 0 }
                }
                aria-hidden={!isItemOpen(index)}
                inert={!isItemOpen(index)}
                className="overflow-hidden"
              >
                <RichText
                  field={item.answer}
                  components={{
                    paragraph: ({ children }) => <p className="pl-10 text-[17px]">{children}</p>,
                  }}
                />
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default FaQ
